/**
 * The Fight List. The screen the whole PvP layer was built and unreachable behind.
 *
 * Every formula in `shared/formulas.ts` section 7.2 shipped at Phase 6 close with ZERO callers,
 * and fifty-five artworks were commissioned and rendered by nothing. This file and `Hitlist.tsx`
 * are where they land.
 *
 * **THE CLIENT DOES NO ARITHMETIC HERE. NOT ONE NUMBER.** The win band, the Fair Fight band, the
 * remaining intents, the hospital timer and `attackable` with its refusal sentence all arrive
 * pre-computed for this viewer, on every row. That is DESIGN-DELTAS 1o.2 and 1j.12: publish
 * everything a userscript would compute, to every account, never as a pass perk, because a script
 * that logs its own fights recovers those numbers inside a few dozen of them and hiding them buys
 * opacity against honest players only.
 *
 * **WHAT IS DELIBERATELY NOT HERE.** A rival's LEVEL-AND-RANK component and their Family bonus.
 * `FightTarget` has no field that could carry either, which is 7.2's "hidden allocation, visible
 * gear" enforced by the wire rather than by this file remembering to subtract. `gearPower` is the
 * only power figure anybody sees.
 *
 * **THE REFUSAL IS PRINTED BEFORE THE TAP, ALWAYS.** A row that cannot be attacked says why, in
 * the same sentence the server would answer with, because the list and the gate call the same
 * function. A dead button that does not say why is the thing this codebase keeps finding.
 */

import { useEffect, useState } from 'react'
import type { FightTarget, PvpIntent } from '@ashport/shared'
import { LINE_NAME } from '@ashport/shared'
import { A } from '../art'
import type { KnownAssetId } from '../art'
import { Icon } from '../ui/Icon'
import { Art, Btn, Chip, Col, Meter, Panel, PanelHead, Rail, Row, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { clock, useGame } from '../game/state'
import { useSession } from '../game/session'

const pct = (bp: number) => `${(bp / 100).toFixed(0)}%`
const money = (c: number) => `$${Math.round(c / 100).toLocaleString('en-US')}`

function Empty({ line, note }: { line: string; note: string }) {
  return (
    <Col
      gap={7}
      style={{
        flex: 1,
        minHeight: 0,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '26px 18px',
        textAlign: 'center',
      }}
    >
      <Icon n="hitlist" s={22} style={{ opacity: 0.4 }} />
      <div className="t-head" style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.12em' }}>
        {line}
      </div>
      <p style={{ margin: 0, maxWidth: 440, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
        {note}
      </p>
    </Col>
  )
}

/**
 * The result plate, which is one of the eight commissioned `pvp.results.*` artworks.
 *
 * The headline and the body are the SERVER's, verbatim. A fight happens once and this response is
 * the only place it is described, so a screen that re-wrote the sentence would be inventing the
 * one thing it cannot reproduce.
 */
function Outcome() {
  const out = useSession((s) => s.lastFight)
  const clear = useSession((s) => s.clearFight)
  if (!out) return null
  const won = out.kind === 'clean_win' || out.kind === 'narrow_win'
  return (
    <Panel tone={won ? 'gold' : 'danger'} style={{ flex: 'none' }}>
      <Art src={A(out.artId as KnownAssetId, 'pvp.results.failed_attack')} alt="" style={{ height: 92 }}>
        <Row gap={10} style={{ position: 'absolute', inset: 0, alignItems: 'flex-end', padding: 10 }}>
          <Icon n="hitlist" s={20} c={won ? 'var(--gold)' : 'var(--arrest)'} />
          <Col gap={2} style={{ flex: 1, minWidth: 0 }}>
            <div className="t-display" style={{ fontSize: 16, color: 'var(--ink-hi)' }}>
              {out.headline}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>{out.body}</div>
          </Col>
          <Btn size="sm" v="ghost" onClick={clear}>
            Done
          </Btn>
        </Row>
      </Art>
      <Row gap={6} style={{ padding: '7px 10px', flexWrap: 'wrap' }}>
        {out.stolen > 0 && <Chip k="dirty">Took {money(out.stolen)}</Chip>}
        {out.heatDeltaMe > 0 && <Chip k="heat">+{out.heatDeltaMe} Heat on you</Chip>}
        {out.heatDeltaThem > 0 && <Chip k="heat">+{out.heatDeltaThem} Heat on them</Chip>}
        {out.bountyClaimed?.pending && <Chip k="gold">Claim filed. It settles in ninety seconds.</Chip>}
        {/*
          * The word of the Era keystream this fight drew from. It is on the screen for the same
          * reason `crime_log.roll_word` is stored: the secret is revealed at Era close and a
          * player walks their own history row by row. A roll nobody can point at is a promise.
          */}
        <Chip>
          Rolled {pct(out.winBp)} band, word {out.rollWord}
        </Chip>
      </Row>
    </Panel>
  )
}

/** The eight off-switches, as a list the player reads. GAME-DESIGN 7.5 rule 5 requires this. */
function Protection() {
  const p = useSession((s) => s.pvp?.protection)
  if (!p) return null
  return (
    <Panel tone={p.standing ? 'safe' : undefined} style={{ flex: 'none' }}>
      <PanelHead
        title={p.standing ? 'Nobody can touch you' : 'You are in the open'}
        icon="lock"
        accent={p.standing ? 'var(--safe)' : 'var(--ink-dim)'}
        sub={p.standing ? 'Any one of these ends it' : 'And it does not come back'}
      />
      <Col gap={3} style={{ padding: '6px 10px 9px' }}>
        {p.conditions.map((c) => (
          <Row key={c.id} gap={7} style={{ alignItems: 'center' }}>
            <Icon
              n={c.met ? 'close' : 'chev'}
              s={11}
              c={c.met ? 'var(--arrest)' : 'var(--ink-dim)'}
            />
            <span
              style={{
                fontSize: 11.5,
                color: c.met ? 'var(--ink-hi)' : 'var(--ink-mid)',
                textDecoration: c.met ? 'line-through' : undefined,
              }}
            >
              {c.label}
            </span>
            {c.id === p.endedBy && <Chip k="heat">this one</Chip>}
          </Row>
        ))}
      </Col>
    </Panel>
  )
}

/** The 6-hour block, and the 72 hour notice on changing it. 7.5 rule 4. */
function Sleep() {
  const { now } = useGame()
  const s = useSession((x) => x.pvp?.sleep)
  const busy = useSession((x) => x.busy)
  const nominate = useSession((x) => x.nominateSleep)
  const [hour, setHour] = useState<number | null>(null)
  if (!s) return null
  const end = (s.startHour + s.hours) % 24
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title="When you sleep"
        icon="clock"
        sub="Attacks still land, for a quarter"
        right={s.active ? <Chip k="safe">open now</Chip> : undefined}
      />
      <Col gap={7} style={{ padding: '7px 10px 10px' }}>
        <Row gap={8} style={{ alignItems: 'baseline' }}>
          <span className="t-mono" style={{ fontSize: 19, color: 'var(--gold)' }}>
            {String(s.startHour).padStart(2, '0')}:00 to {String(end).padStart(2, '0')}:00
          </span>
          <span className="t-label" style={{ fontSize: sz(8.5) }}>UTC</span>
          {s.isDefault && <Chip>ours, until you pick</Chip>}
        </Row>
        {s.pendingHour >= 0 ? (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            You have filed for {String(s.pendingHour).padStart(2, '0')}:00. It lands{' '}
            {clock((s.pendingAt - now) / 1000)} from now. Nobody moves their window mid fight.
          </p>
        ) : s.changeable ? (
          <Row gap={6} style={{ flexWrap: 'wrap' }}>
            <select
              className="t-mono"
              value={hour ?? s.startHour}
              onChange={(e) => setHour(Number(e.target.value))}
              style={{ background: 'var(--ink-well)', color: 'var(--ink)', padding: '3px 6px', fontSize: 11 }}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
            <Btn
              size="sm"
              v="gold"
              disabled={busy}
              onClick={() => void nominate(hour ?? s.startHour)}
              title="Takes effect in 72 hours"
            >
              File it
            </Btn>
            {/*
              THE COUNT, RATHER THAN THE WORD "ONE". 7.5 rule 4 gives an account two writes of the
              hour, the nomination and the change, and this line said "one" while the server allowed
              a different number. A player cannot tell whether they are spending their last move
              from a boolean, so the server publishes `changesLeft` and the screen states it.
            */}
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
              {s.changesLeft === 1 ? 'Your last move this Era' : `${s.changesLeft ?? 1} moves left this Era`}
              , and it takes 72 hours.
            </span>
          </Row>
        ) : (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)' }}>
            You have moved it twice. That is all the Era gives you.
          </p>
        )}
      </Col>
    </Panel>
  )
}

function TargetRow({ t, intent }: { t: FightTarget; intent: PvpIntent }) {
  const busy = useSession((s) => s.busy)
  const attack = useSession((s) => s.attack)
  return (
    <Row
      gap={9}
      style={{
        alignItems: 'center',
        padding: '6px 8px',
        borderTop: '1px solid var(--rule)',
        opacity: t.attackable ? 1 : 0.62,
      }}
    >
      <img
        src={A(t.portraitId as KnownAssetId, 'characters.generic.vito_carnera')}
        alt=""
        style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 2, flex: 'none' }}
      />
      <Col gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Row gap={6} style={{ alignItems: 'baseline', flexWrap: 'wrap', rowGap: 2 }}>
          <span className="t-head" style={{ fontSize: 12.5, color: 'var(--ink-hi)' }}>
            {t.displayName}
          </span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)' }}>
            L{t.level} {t.rank}
          </span>
          {/* PHASE 24. The Line they drew: the one thing two Soldiers at a level differ by. */}
          {t.line !== '' && <Chip c="var(--brass-lit)">{LINE_NAME[t.line]}</Chip>}
          {t.asleep && <Chip k="safe">asleep</Chip>}
          {t.bountyTotal > 0 && <Chip k="gold">{money(t.bountyTotal)} on their head</Chip>}
        </Row>
        {/*
          * Gear power ONLY, and the label says so out loud rather than leaving it to be assumed.
          * 7.2: losing to a lower apparent power tells you something you did not know.
          */}
        <Row gap={8} style={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-mid)' }}>
            kit {t.gearPower}
          </span>
          <div style={{ flex: 1, maxWidth: 90 }}>
            <Meter v={t.winBp} max={10_000} a="var(--gold)" h={3} />
          </div>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--gold)' }}>
            {pct(t.winBp)} to win
          </span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)' }}>
            fair fight {pct(t.fairFightBp)}
          </span>
        </Row>
        {t.refusal && (
          <span style={{ fontSize: sz(10.5), color: 'var(--arrest)', lineHeight: 1.4 }}>{t.refusal}</span>
        )}
      </Col>
      <Col gap={3} style={{ flex: 'none', alignItems: 'flex-end' }}>
        <Btn
          size="sm"
          v={t.attackable ? 'danger' : 'ghost'}
          disabled={!t.attackable || busy}
          onClick={() => void attack(t.playerId, intent)}
          title={t.refusal ?? undefined}
        >
          Go
        </Btn>
        <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-dim)' }}>
          {t.intentsLeft}/{t.intentCap} left
        </span>
      </Col>
    </Row>
  )
}

/* ------------------------------------------------------------------ screen */

export function Fights() {
  useGame()
  const p = useSession((s) => s.pvp)
  const load = useSession((s) => s.loadPvp)
  const [intent, setIntent] = useState<PvpIntent>('mug')

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  const chosen = p?.intents.find((i) => i.id === intent)
  const { stacked } = useViewport()
  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 9px', flexWrap: 'wrap' }}>
            <Icon n="hitlist" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none' }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>
                The Street
              </div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>
                Everybody inside twelve levels of you
              </div>
            </div>
            <div style={{ flex: 1 }} />
            {p && (
              <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                your kit {p.myGearPower}
              </span>
            )}
          </Row>
        </Panel>

        <Outcome />

        {/*
          * DECLARED BEFORE THE ROLL. Mafia Wars and Torn both let the attacker choose the outcome
          * AFTER winning, which turns every attack into a strictly optimal pick. Choosing here,
          * before any row is tapped, is a real decision and it is the reason this control sits
          * above the list rather than inside each row as three buttons.
          */}
        {p && (
          <Panel style={{ flex: 'none' }}>
            <Row gap={8} style={{ padding: '7px 10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Tabs
                items={p.intents.map((i) => ({ id: i.id, label: i.label }))}
                value={intent}
                onChange={setIntent}
              />
              {chosen && (
                <>
                  <Chip k="nerve">{chosen.nerve} Nerve</Chip>
                  <span style={{ fontSize: 11, color: 'var(--ink-mid)', flex: 1, minWidth: 180 }}>
                    {chosen.blurb}
                  </span>
                </>
              )}
            </Row>
          </Panel>
        )}

        <Panel style={{ flex: 1, minHeight: 0 }}>
          <PanelHead
            title="In your bracket"
            icon="hitlist"
            sub="Most recently about, first"
            right={
              p ? (
                <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
                  L{Math.max(1, p.bracket.from)} to L{p.bracket.to}
                </span>
              ) : undefined
            }
          />
          {!p ? (
            <Empty line="Asking around" note="One moment." />
          ) : p.targets.length === 0 ? (
            <Empty
              line="Nobody your size is out"
              note="The bracket is twelve levels either way. Come back when the city is busier, or grow into a wider one."
            />
          ) : (
            <div className="scroll-y" style={{ flex: 1 }}>
              {p.targets.map((t) => (
                <TargetRow key={t.playerId} t={t} intent={intent} />
              ))}
            </div>
          )}
        </Panel>
      </Col>

      <Rail width={300}>
        <Protection />
        <Sleep />
        {p && p.ambush && (
          <Panel tone="danger" style={{ flex: 'none' }}>
            <PanelHead title="You have something set" icon="hitlist" accent="var(--arrest)" />
            <p style={{ margin: 0, padding: '7px 10px 10px', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
              Waiting on {p.ambush.againstHandle}. Four hours flat, whatever they are carrying.
            </p>
          </Panel>
        )}
        {p && p.beefs.length > 0 && (
          <Panel style={{ flex: 1, minHeight: 0 }}>
            <PanelHead title="Who you are not speaking to" icon="hitlist" sub="Permanent, this Era" />
            <div className="scroll-y" style={{ flex: 1, padding: '4px 8px 8px' }}>
              {p.beefs.map((b) => (
                <Row key={b.otherId} gap={7} style={{ padding: '5px 0', alignItems: 'center' }}>
                  <img
                    src={A(b.stateIconId as KnownAssetId, 'icons.beef.opened')}
                    alt=""
                    style={{ width: 16, height: 16, flex: 'none' }}
                  />
                  <span style={{ fontSize: 11.5, color: 'var(--ink-hi)', flex: 1, minWidth: 0 }}>
                    {b.otherHandle}
                  </span>
                  <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
                    {b.scoreMe} to {b.scoreThem}
                  </span>
                </Row>
              ))}
            </div>
          </Panel>
        )}
      </Rail>
    </Stack>
  )
}
